import type Phaser from "phaser";
import { audioManager } from "../../../audio/AudioManager";
import { BORG, PHASER } from "../../../config";
import type { BorgCubeSprite } from "../BorgCube";

// Sprite orientation offset: sprite points DOWN, Phaser rotation=0 is RIGHT
const SPRITE_ANGLE_OFFSET = Math.PI / 2;

/**
 * Normalize angle to [-PI, PI] range
 */
function normalizeAngle(input: number): number {
  let angle = input;
  while (angle > Math.PI) angle -= 2 * Math.PI;
  while (angle < -Math.PI) angle += 2 * Math.PI;
  return angle;
}

/**
 * Ray-circle intersection test.
 * Returns distance along ray to first intersection, or null if no hit.
 */
function rayCircleIntersect(
  rayX: number,
  rayY: number,
  dirX: number,
  dirY: number,
  circleX: number,
  circleY: number,
  radius: number,
): number | null {
  // Vector from ray origin to circle center
  const ocX = circleX - rayX;
  const ocY = circleY - rayY;

  // Project onto ray direction
  const proj = ocX * dirX + ocY * dirY;

  // Closest point on ray to circle center
  const closestX = rayX + dirX * proj;
  const closestY = rayY + dirY * proj;

  // Distance from closest point to circle center
  const distX = circleX - closestX;
  const distY = circleY - closestY;
  const distSq = distX * distX + distY * distY;

  if (distSq > radius * radius) {
    return null; // Ray misses circle
  }

  // Distance from closest point back to intersection
  const halfChord = Math.sqrt(radius * radius - distSq);

  // First intersection (entering the circle)
  const t = proj - halfChord;

  // Only return if intersection is in front of ray origin
  return t > 0 ? t : null;
}

interface BeamHit {
  target: BorgCubeSprite;
  hitX: number;
  hitY: number;
  distance: number;
}

export class PhaserBeamSystem {
  private scene: Phaser.Scene;
  private ship: Phaser.Physics.Arcade.Sprite;
  private borgGroup: Phaser.Physics.Arcade.Group;

  // The borg we're AIMING at (acquired on key-down)
  private aimTarget: BorgCubeSprite | null = null;
  private graphics!: Phaser.GameObjects.Graphics;
  private firing = false;
  private damageAccum = 0;

  // Callback for when phaser damages a target
  onDamage: ((target: BorgCubeSprite, damage: number) => void) | null = null;

  constructor(
    scene: Phaser.Scene,
    ship: Phaser.Physics.Arcade.Sprite,
    borgGroup: Phaser.Physics.Arcade.Group,
  ) {
    this.scene = scene;
    this.ship = ship;
    this.borgGroup = borgGroup;
  }

  create(): void {
    this.graphics = this.scene.add.graphics();
    this.graphics.setDepth(5);
  }

  /**
   * Called on key-down: try to acquire a target to aim at.
   * Returns true if target acquired.
   */
  tryAcquireTarget(): boolean {
    // Already firing? Ignore (must release first)
    if (this.firing) {
      return true;
    }

    // Find target to aim at
    const target = this.findAimTarget();
    if (!target) {
      audioManager.playPhaserDenied();
      return false;
    }

    // Start firing at this target
    this.aimTarget = target;
    this.firing = true;
    this.damageAccum = 0;
    audioManager.startPhaserLoop();

    return true;
  }

  /**
   * Called on key-up: stop firing.
   */
  stop(): void {
    if (this.firing) {
      this.firing = false;
      this.aimTarget = null;
      this.graphics.clear();
      audioManager.stopPhaserLoop();
    }
  }

  /**
   * Update phaser beam each frame.
   * Ray casts toward aim target to find what's actually hit.
   */
  update(delta: number): void {
    if (!this.firing || !this.aimTarget) {
      this.graphics.clear();
      return;
    }

    // Check if aim target is still valid (in range/cone)
    if (!this.isAimTargetValid(this.aimTarget)) {
      this.firing = false;
      this.aimTarget = null;
      this.graphics.clear();
      audioManager.stopPhaserLoop();
      return;
    }

    // Ray cast from ship toward aim target to find what we actually hit
    const hit = this.raycastTowardTarget(this.aimTarget);

    if (hit) {
      // Apply damage to whatever the beam actually hits
      const damageThisFrame = (PHASER.DPS * delta) / 1000;
      this.damageAccum += damageThisFrame;

      if (this.damageAccum >= 1 && this.onDamage) {
        const wholeDamage = Math.floor(this.damageAccum);
        this.onDamage(hit.target, wholeDamage);
        this.damageAccum -= wholeDamage;
      }

      // Draw beam to hit point
      this.drawBeam(hit.hitX, hit.hitY);
    } else {
      // Beam reaches toward target but hits nothing (shouldn't happen often)
      this.drawBeam(this.aimTarget.x, this.aimTarget.y);
    }
  }

  isFiring(): boolean {
    return this.firing;
  }

  /**
   * Called when a borg is destroyed. If it was our aim target, stop firing.
   */
  notifyTargetDestroyed(target: BorgCubeSprite): void {
    if (this.aimTarget === target) {
      this.firing = false;
      this.aimTarget = null;
      this.graphics.clear();
      audioManager.stopPhaserLoop();
    }
  }

  /**
   * Find closest borg in acquisition cone to aim at.
   */
  private findAimTarget(): BorgCubeSprite | null {
    const shipX = this.ship.x;
    const shipY = this.ship.y;
    const shipAngle = this.ship.rotation + SPRITE_ANGLE_OFFSET;

    const candidates: { borg: BorgCubeSprite; distance: number }[] = [];

    this.borgGroup.children.each((child) => {
      const borg = child as BorgCubeSprite;
      if (!borg.active) return true;

      const dx = borg.x - shipX;
      const dy = borg.y - shipY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > PHASER.RANGE) return true;

      const angleToTarget = Math.atan2(dy, dx);
      const angleDiff = normalizeAngle(angleToTarget - shipAngle);

      if (Math.abs(angleDiff) > PHASER.CONE_HALF_ANGLE) return true;

      candidates.push({ borg, distance });
      return true;
    });

    if (candidates.length === 0) return null;
    candidates.sort((a, b) => a.distance - b.distance);
    return candidates[0]?.borg ?? null;
  }

  /**
   * Check if aim target is still valid (with hysteresis for release).
   */
  private isAimTargetValid(target: BorgCubeSprite): boolean {
    if (!target.active) return false;

    const shipX = this.ship.x;
    const shipY = this.ship.y;
    const shipAngle = this.ship.rotation + SPRITE_ANGLE_OFFSET;

    const dx = target.x - shipX;
    const dy = target.y - shipY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > PHASER.RELEASE_RANGE) return false;

    const angleToTarget = Math.atan2(dy, dx);
    const angleDiff = normalizeAngle(angleToTarget - shipAngle);

    if (Math.abs(angleDiff) > PHASER.RELEASE_CONE_HALF_ANGLE) return false;

    return true;
  }

  /**
   * Ray cast from ship toward aim target.
   * Returns the first borg hit (which may not be the aim target).
   */
  private raycastTowardTarget(aimTarget: BorgCubeSprite): BeamHit | null {
    const shipX = this.ship.x;
    const shipY = this.ship.y;

    // Direction toward aim target
    const dx = aimTarget.x - shipX;
    const dy = aimTarget.y - shipY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist === 0) return null;

    const dirX = dx / dist;
    const dirY = dy / dist;

    // Find first borg hit along this ray
    let closestHit: BeamHit | null = null;

    this.borgGroup.children.each((child) => {
      const borg = child as BorgCubeSprite;
      if (!borg.active) return true;

      // Get borg radius based on size
      const radius = this.getBorgRadius(borg);

      const hitDist = rayCircleIntersect(
        shipX,
        shipY,
        dirX,
        dirY,
        borg.x,
        borg.y,
        radius,
      );

      if (hitDist !== null) {
        if (!closestHit || hitDist < closestHit.distance) {
          closestHit = {
            target: borg,
            hitX: shipX + dirX * hitDist,
            hitY: shipY + dirY * hitDist,
            distance: hitDist,
          };
        }
      }

      return true;
    });

    return closestHit;
  }

  /**
   * Get collision radius for a borg based on its size.
   */
  private getBorgRadius(borg: BorgCubeSprite): number {
    switch (borg.borgSize) {
      case "large":
        return BORG.LARGE.size / 2;
      case "medium":
        return BORG.MEDIUM.size / 2;
      case "small":
        return BORG.SMALL.size / 2;
      default:
        return 20;
    }
  }

  /**
   * Draw the phaser beam to hit point.
   */
  private drawBeam(hitX: number, hitY: number): void {
    const shipX = this.ship.x;
    const shipY = this.ship.y;

    this.graphics.clear();

    // Outer glow
    this.graphics.lineStyle(PHASER.BEAM_WIDTH + 4, PHASER.BEAM_GLOW_COLOR, 0.3);
    this.graphics.beginPath();
    this.graphics.moveTo(shipX, shipY);
    this.graphics.lineTo(hitX, hitY);
    this.graphics.strokePath();

    // Core beam
    this.graphics.lineStyle(PHASER.BEAM_WIDTH, PHASER.BEAM_COLOR, 0.9);
    this.graphics.beginPath();
    this.graphics.moveTo(shipX, shipY);
    this.graphics.lineTo(hitX, hitY);
    this.graphics.strokePath();

    // Bright center
    this.graphics.lineStyle(2, 0xffffaa, 1);
    this.graphics.beginPath();
    this.graphics.moveTo(shipX, shipY);
    this.graphics.lineTo(hitX, hitY);
    this.graphics.strokePath();

    // Impact flash
    const flashSize = 6 + Math.random() * 4;
    this.graphics.fillStyle(0xffffff, 0.8);
    this.graphics.fillCircle(hitX, hitY, flashSize);
    this.graphics.fillStyle(PHASER.BEAM_COLOR, 0.5);
    this.graphics.fillCircle(hitX, hitY, flashSize + 4);
  }

  hide(): void {
    this.stop();
  }

  destroy(): void {
    this.stop();
    this.graphics.destroy();
  }
}
